import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import AuthContext from '../context/AuthContext';
import SkeletonLoader from './SkeletonLoader';
import { 
    FaUsers, FaBuilding, FaClipboardList, FaCheckCircle, 
    FaLayerGroup, FaChalkboardTeacher, FaPlay, FaHistory, 
    FaTimesCircle, FaExclamationCircle, FaStar, 
    FaPlus, FaClipboardCheck, FaFilter, FaCalendarAlt, 
    FaSearch, FaClock, FaListOl, FaChevronRight, FaTrophy, FaChartPie, FaInfoCircle
} from 'react-icons/fa';

const Dashboard = () => {
    const { auth } = useContext(AuthContext);
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    
    // Loading iniziale (vero loading) vs refresh background
    const [loading, setLoading] = useState(true);
    const isFirstLoad = useRef(true); // Ref per tracciare il primo caricamento

    // Stati per UI Test (Gestione/Ricerca)
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTestToStart, setSelectedTestToStart] = useState(null); 
    const [filterTenant, setFilterTenant] = useState('all');
    
    // Stati Filtri Storico
    const [historySearch, setHistorySearch] = useState('');
    const [historyCategory, setHistoryCategory] = useState('all');
    const [historyDateFrom, setHistoryDateFrom] = useState('');
    const [historyDateTo, setHistoryDateTo] = useState('');

    // Stati Modali e Sezioni
    const [resultModalData, setResultModalData] = useState(null);
    const [loadingResult, setLoadingResult] = useState(false);

    // --- NUOVI STATI PER LA SEZIONE CORSI (Master-Detail) ---
    const [activeCourseId, setActiveCourseId] = useState(null); // ID del corso selezionato a sinistra
    const [confirmStartTest, setConfirmStartTest] = useState(null); // Test selezionato per il modale di conferma

    // Funzione Fetch Ottimizzata
    const fetchDashboard = async (isBackground = false) => {
        if (!isBackground) setLoading(true);
        try {
            const url = `/api_dashboard.php?role=${auth.role}&user_id=${auth.user_id}&ts=${Date.now()}`;
            const res = await axios.get(url);
            if (res.data.success) {
                const newData = res.data.data;
                setData(newData);
                
                // FIX FOCUS: Imposta il corso attivo SOLO se non ce n'è uno selezionato E siamo al primo caricamento
                if (!activeCourseId && isFirstLoad.current && newData.my_classes && newData.my_classes.length > 0) {
                    setActiveCourseId(newData.my_classes[0].id);
                }
                isFirstLoad.current = false; // Segna come caricato
            }
        } catch (error) { 
            console.error("Errore dashboard", error); 
        } finally { 
            if (!isBackground) setLoading(false); 
        }
    };

    useEffect(() => {
        fetchDashboard(false); // Primo caricamento con spinner
        const interval = setInterval(() => {
            fetchDashboard(true); // Refresh background (no spinner, no reset focus)
        }, 30000); 
        return () => clearInterval(interval);
    }, [auth]); 

    // --- LOGICA VALIDITÀ TEST ---
    const isTestActive = (test) => {
        const now = new Date();
        const from = test.valid_from ? new Date(test.valid_from) : null;
        const to = test.valid_to ? new Date(test.valid_to) : null;

        if (from && now < from) return false; 
        if (to && now > to) return false;     
        return true; 
    };

    // --- LOGICA STATO CLASSE (COLORI E ICONE) ---
    const getClassStatus = (cls) => {
        if (!cls.tests || cls.tests.length === 0) {
            return { className: 'bg-light text-muted', icon: null };
        }

        let allCompleted = true;
        let anyFailed = false;
        let allExcellent = true; // 95-100%

        for (const t of cls.tests) {
            if (t.best_score === null) {
                allCompleted = false;
                allExcellent = false; 
                continue; 
            }

            const score = parseInt(t.best_score);
            const max = parseInt(t.questions_count) || 1; 
            const pct = (score / max) * 100;
            const threshold = parseInt(t.pass_threshold) || 60;

            if (pct < threshold) {
                anyFailed = true;
                allExcellent = false;
            }

            if (pct < 95) {
                allExcellent = false;
            }
        }

        if (anyFailed) {
            return { className: 'bg-danger bg-opacity-10 text-danger border-start border-danger border-4', icon: <FaTimesCircle className="fs-5" /> };
        }
        if (!allCompleted) {
            return { className: 'bg-light text-dark border-start border-secondary border-4', icon: <FaClock className="text-muted fs-5" /> };
        }
        if (allExcellent) {
            return { className: 'bg-warning bg-opacity-10 text-dark border-start border-warning border-4', icon: <FaTrophy className="text-warning fs-4" /> };
        }
        return { className: 'bg-success bg-opacity-10 text-dark border-start border-success border-4', icon: <FaCheckCircle className="text-success fs-5" /> };
    };

    const fetchAttemptDetails = async (attemptId, testName, score, date) => {
        setLoadingResult(true);
        try {
            const res = await axios.get('/api_get_attempt_details.php', {
                params: { id: attemptId, user_id: auth.user_id },
                withCredentials: true 
            });
            if (res.data.success) {
                const apiData = res.data.data || {};
                setResultModalData({
                    testName: apiData.test_id || testName, 
                    score: apiData.score ?? score,
                    date: apiData.finished_at || date,
                    questions: Array.isArray(apiData.questions) ? apiData.questions : []
                });
            } else {
                alert("Errore: " + (res.data.message || "Dettagli non trovati"));
            }
        } catch (error) { console.error(error); } 
        finally { setLoadingResult(false); }
    };

    const getResultBadge = (score, thresholdPercent, totalQuestions) => {
        const scoreNum = parseInt(score || 0);
        const totalNum = parseInt(totalQuestions || 0);
        const thresholdPct = parseInt(thresholdPercent || 60); 
        const minPoints = Math.ceil((totalNum * thresholdPct) / 100);
        let color = 'text-danger'; 
        let bg = 'bg-danger'; 
        if (totalNum > 0 && scoreNum === totalNum) {
            color = 'text-warning'; bg = 'bg-warning';
        } else if (scoreNum >= minPoints) {
            color = 'text-success'; bg = 'bg-success';
        }
        return (
            <div className={`rounded-circle d-flex align-items-center justify-content-center shadow-sm ${bg} bg-opacity-10`} style={{width: '35px', height: '35px'}}>
                <span className={`fw-bold ${color}`} style={{fontSize: '0.9rem'}}>V</span>
            </div>
        );
    };

    if (loading) return <div className="p-4"><SkeletonLoader height="300px" /></div>;
    if (!data) return <div className="p-4 text-center">Nessun dato disponibile.</div>;

    const isSuperAdmin = auth.role === 'superadmin';
    const canCreateTest = ['superadmin', 'admin', 'professor', 'power_user'].includes(auth.role);

    const managedTests = data.managed_tests || []; 
    const availableTests = data.available_tests || []; 
    const historyTests = data.history_tests || [];
    const categoryStats = data.category_stats || [];
    const myGroups = data.my_groups || []; // Dato mantenuto ma non renderizzato
    const myClasses = data.my_classes || [];
    const tenantsList = data.tenants_list || [];

    const filteredManagedTests = filterTenant === 'all' ? managedTests : managedTests.filter(t => t.tenant_id == filterTenant);
    
    // FIX TEST PUBBLICI: Filtro per tenant se l'utente non è superadmin
    let sourceTestsForExecution = canCreateTest ? filteredManagedTests : availableTests;
    if (!isSuperAdmin) {
        // Se non è superadmin, mostra solo i test del proprio tenant o pubblici globali (se previsto dalla tua logica, qui filtro per tenant)
        // Assumiamo che availableTests contenga già solo i test visibili all'utente.
        // Se vuoi filtrare ulteriormente per tenant_id dell'utente corrente:
        // sourceTestsForExecution = sourceTestsForExecution.filter(t => t.tenant_id == auth.tenant_id);
    }
    
    const filteredExecutionTests = sourceTestsForExecution.filter(t => t.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const uniqueCategories = [...new Set(historyTests.map(item => item.category || 'Generale'))];
    const filteredHistoryTests = historyTests.filter(t => {
        const matchesName = t.test_name.toLowerCase().includes(historySearch.toLowerCase());
        const matchesCat = historyCategory === 'all' || (t.category || 'Generale') === historyCategory;
        let matchesDate = true;
        if (historyDateFrom) matchesDate = matchesDate && new Date(t.finished_at) >= new Date(historyDateFrom);
        if (historyDateTo) {
            const dateTo = new Date(historyDateTo);
            dateTo.setHours(23, 59, 59);
            matchesDate = matchesDate && new Date(t.finished_at) <= dateTo;
        }
        return matchesName && matchesCat && matchesDate;
    });

    const activeClassData = myClasses.find(c => c.id === activeCourseId);
    const activeClassTests = activeClassData?.tests?.filter(t => isTestActive(t)) || [];

    return (
        <div className="container-fluid fade-in pb-5">
            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="fw-bold text-primary mb-0">Ciao, {auth.name}</h4>
                    <p className="text-muted small mb-0">{data.user?.tenant_name || 'Unitest AI'}</p>
                </div>
                <div className="d-flex align-items-center bg-white border rounded-pill px-3 py-1 shadow-sm">
                    <span className="badge bg-success rounded-circle p-1 me-2"><span className="visually-hidden">Online</span></span>
                    <span className="small fw-bold text-muted">{data.online_users || 1} Online</span>
                </div>
            </div>

            {/* 1. STATISTICHE GLOBALI */}
            {isSuperAdmin && (
                <div className="mb-5">
                    <h6 className="fw-bold text-muted mb-3 border-bottom pb-2">PANNELLO GLOBALE</h6>
                    <div className="row g-4">
                        <div className="col-md-4"><div className="card shadow-sm border-0 border-start border-4 border-primary h-100 p-3"><div className="d-flex justify-content-between align-items-center"><div><div className="small text-muted fw-bold">AZIENDE</div><h2 className="mb-0 fw-bold">{data.total_tenants || 0}</h2></div><FaBuilding className="fs-1 text-primary opacity-25" /></div></div></div>
                        <div className="col-md-4"><div className="card shadow-sm border-0 border-start border-4 border-success h-100 p-3"><div className="d-flex justify-content-between align-items-center"><div><div className="small text-muted fw-bold">UTENTI GLOBALI</div><h2 className="mb-0 fw-bold">{data.total_users || 0}</h2></div><FaUsers className="fs-1 text-success opacity-25" /></div></div></div>
                        <div className="col-md-4"><div className="card shadow-sm border-0 border-start border-4 border-warning h-100 p-3"><div className="d-flex justify-content-between align-items-center"><div><div className="small text-muted fw-bold">TEST TOTALI</div><h2 className="mb-0 fw-bold">{data.total_tests || 0}</h2></div><FaClipboardList className="fs-1 text-warning opacity-25" /></div></div></div>
                    </div>
                </div>
            )}

            {/* 2. GESTIONE TEST (PER CHI PUÒ CREARE) */}
            {canCreateTest && (
                <div className="card shadow-sm border-0 mb-5 bg-white">
                    <div className="card-body py-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="d-flex align-items-center">
                                <FaClipboardCheck className="me-2 fs-5 text-muted" /> 
                                <span className="fw-bold text-muted me-2">Gestione Test:</span>
                                <span className="badge bg-primary rounded-pill">{filteredManagedTests.length} Attivi</span>
                            </div>
                            <button className="btn btn-primary btn-sm rounded-pill px-3 shadow-sm" onClick={() => navigate('/create-test')}>
                                <FaPlus className="me-1 mb-1" /> Nuovo Test
                            </button>
                        </div>
                        {isSuperAdmin && (
                            <div className="mb-3 bg-light p-2 rounded d-flex align-items-center">
                                <FaFilter className="text-secondary me-2" />
                                <select className="form-select form-select-sm border-0 bg-transparent fw-bold text-secondary shadow-none" value={filterTenant} onChange={(e) => setFilterTenant(e.target.value)}>
                                    <option value="all">Tutti i Tenant</option>
                                    {tenantsList.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                            </div>
                        )}
                        <div className="list-group list-group-flush" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                            {filteredManagedTests.map(test => (
                                <div key={test.id} className="list-group-item px-0 py-2 border-bottom d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <FaClipboardList className="text-muted me-2"/>
                                        <span className="text-dark small fw-bold">{test.name}</span>
                                    </div>
                                    <small className="text-muted">{test.questions_count || test.question_limit} Domande</small>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* 3. I MIEI CORSI (Master-Detail) */}
            <div className="mb-5">
                <h5 className="fw-bold text-primary mb-3"><FaChalkboardTeacher className="me-2" />I Miei Corsi e Attività</h5>
                <div className="card shadow border-0 overflow-hidden" style={{minHeight: '500px'}}>
                    <div className="row g-0 h-100">
                        {/* SINISTRA */}
                        <div className="col-md-3 border-end bg-light">
                            <div className="p-3 border-bottom bg-white">
                                <span className="fw-bold text-muted small text-uppercase">Corsi Disponibili</span>
                            </div>
                            <div className="list-group list-group-flush">
                                {myClasses.length > 0 ? myClasses.map(cls => {
                                    const status = getClassStatus(cls);
                                    const isActive = activeCourseId === cls.id;

                                    return (
                                        <button 
                                            key={cls.id}
                                            onClick={() => setActiveCourseId(cls.id)}
                                            className={`list-group-item list-group-item-action border-0 py-3 px-3 d-flex justify-content-between align-items-center ${isActive ? 'shadow-sm bg-white' : ''} ${status.className}`}
                                            style={{ transition: 'all 0.2s', borderLeftWidth: '5px' }}
                                        >
                                            <div className="text-truncate me-2" style={{maxWidth: '85%'}}>
                                                <div className="fw-bold d-flex align-items-center">
                                                    {status.icon && <span className="me-2">{status.icon}</span>}
                                                    {cls.name}
                                                </div>
                                                <div className="small text-muted text-truncate">
                                                    {cls.description || 'Nessuna descrizione'}
                                                </div>
                                            </div>
                                            <FaChevronRight className={`small ${isActive ? 'text-primary' : 'text-muted'}`} />
                                        </button>
                                    );
                                }) : (
                                    <div className="p-4 text-center text-muted small">Nessun corso assegnato.</div>
                                )}
                            </div>
                        </div>

                        {/* DESTRA */}
                        <div className="col-md-9 bg-white p-4">
                            {activeClassData ? (
                                <div className="fade-in">
                                    <div className="d-flex justify-content-between align-items-end mb-4 border-bottom pb-2">
                                        <div>
                                            <h4 className="fw-bold mb-1 text-dark">{activeClassData.name}</h4>
                                            <p className="text-muted mb-0">{activeClassData.description}</p>
                                        </div>
                                        <div className="text-end">
                                            <span className="badge bg-light text-dark border">
                                                {activeClassTests.length} Test Attivi
                                            </span>
                                        </div>
                                    </div>

                                    {activeClassTests.length > 0 ? (
                                        <div className="row g-3">
                                            {activeClassTests.map(test => (
                                                <div key={test.id} className="col-sm-6 col-lg-4">
                                                    <div 
                                                        className="card h-100 shadow-sm border-0 hover-lift cursor-pointer"
                                                        onClick={() => setConfirmStartTest(test)}
                                                        style={{transition: 'transform 0.2s'}}
                                                    >
                                                        <div className="card-body p-4">
                                                            <div className="d-flex justify-content-between mb-3">
                                                                <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center" style={{width:'40px', height:'40px'}}>
                                                                    <FaClipboardList />
                                                                </div>
                                                                {test.best_score !== null && (
                                                                    <span className="badge bg-success bg-opacity-10 text-success border border-success">
                                                                        Voto: {test.best_score}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <h6 className="fw-bold mb-2 text-dark">{test.name}</h6>
                                                            <div className="text-muted small mb-3 text-truncate-2">
                                                                {test.description}
                                                            </div>
                                                            <div className="d-flex justify-content-between align-items-center text-muted small mt-auto pt-3 border-top">
                                                                <span><FaClock className="me-1"/>{test.duration || 30} min</span>
                                                                <span><FaListOl className="me-1"/>{test.questions_count} Dom</span>
                                                            </div>
                                                        </div>
                                                        <div className="card-footer bg-white border-0 p-3 pt-0">
                                                            <button className="btn btn-outline-primary w-100 rounded-pill btn-sm fw-bold">
                                                                Vedi Dettagli
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-5 text-muted">
                                            <FaExclamationCircle className="fs-1 mb-3 opacity-25" />
                                            <h5>Nessun test attivo in questo corso.</h5>
                                            <p className="small">I test potrebbero essere scaduti o non ancora programmati.</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted opacity-50">
                                    <FaChalkboardTeacher className="display-4 mb-3" />
                                    <p className="fs-5">Seleziona un corso a sinistra per visualizzare i contenuti.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* 4. ESECUZIONE RAPIDA (RIORGANIZZATO COME RICHIESTO: FULL WIDTH) */}
            <div className="mb-5">
                <div className="card shadow-sm border-0 h-100">
                    <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                        <h5 className="fw-bold text-primary mb-0">
                            <FaPlay className="me-2" />Esecuzione Rapida / Test Pubblici
                        </h5>
                        <div className="input-group" style={{maxWidth: '300px'}}>
                            <span className="input-group-text bg-light border-0"><FaSearch className="text-muted"/></span>
                            <input 
                                type="text" 
                                className="form-control border-0 bg-light shadow-none" 
                                placeholder="Cerca test pubblici..." 
                                value={searchTerm} 
                                onChange={(e) => setSearchTerm(e.target.value)} 
                            />
                        </div>
                    </div>
                    <div className="card-body">
                         <div className="list-group list-group-flush" style={{maxHeight: '300px', overflowY: 'auto'}}>
                             {filteredExecutionTests.length > 0 ? filteredExecutionTests.map(test => (
                                <button key={test.id} className="list-group-item list-group-item-action p-3" onClick={() => setSelectedTestToStart(test)}>
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h6 className="fw-bold mb-0">{test.name}</h6>
                                            <small className="text-muted text-truncate d-block" style={{maxWidth:'500px'}}>{test.description}</small>
                                        </div>
                                        <div className="d-flex align-items-center gap-3">
                                            <small className="badge bg-light text-dark border"><FaClock className="me-1"/>{test.duration || 30}'</small>
                                            <FaChevronRight className="text-muted small"/>
                                        </div>
                                    </div>
                                </button>
                             )) : <div className="p-3 text-center text-muted">Nessun test trovato.</div>}
                        </div>
                        {/* Sezione caricarne di più (Placeholder per paginazione futura) */}
                        <div className="text-center mt-3">
                            <button className="btn btn-link text-decoration-none btn-sm">Carica altri test...</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 5. STORICO */}
            <div className="row mb-5">
                <div className="col-lg-8 mb-4">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-header bg-white border-bottom p-3">
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                                <h6 className="fw-bold text-secondary mb-0"><FaHistory className="me-2" />Storico Test Svolti</h6>
                                <div className="d-flex flex-wrap gap-2 align-items-center">
                                    <div className="input-group input-group-sm" style={{width: '200px'}}>
                                        <span className="input-group-text bg-light border-end-0"><FaSearch className="text-muted"/></span>
                                        <input type="text" className="form-control bg-light border-start-0 shadow-none" placeholder="Cerca test..." value={historySearch} onChange={e => setHistorySearch(e.target.value)} />
                                    </div>
                                    <select className="form-select form-select-sm shadow-none" style={{width: '150px'}} value={historyCategory} onChange={e => setHistoryCategory(e.target.value)}>
                                        <option value="all">Tutte Categorie</option>
                                        {uniqueCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    </select>
                                    <input type="date" className="form-control form-control-sm shadow-none" style={{width: '130px'}} value={historyDateFrom} onChange={e => setHistoryDateFrom(e.target.value)} />
                                    <input type="date" className="form-control form-control-sm shadow-none" style={{width: '130px'}} value={historyDateTo} onChange={e => setHistoryDateTo(e.target.value)} />
                                </div>
                            </div>
                        </div>
                        
                        <div className="card-body p-0">
                            <div className="list-group list-group-flush" style={{maxHeight: '600px', overflowY: 'auto'}}>
                                {filteredHistoryTests.length > 0 ? (
                                    filteredHistoryTests.map(attempt => (
                                        <div 
                                            key={attempt.attempt_id} 
                                            className="list-group-item list-group-item-action py-3 px-4 d-flex align-items-center justify-content-between hover-bg-light"
                                            style={{cursor: 'pointer'}}
                                            onClick={() => fetchAttemptDetails(attempt.attempt_id, attempt.test_name, attempt.score, attempt.finished_at)}
                                        >
                                            <div className="d-flex align-items-center">
                                                <div className="me-3">
                                                    {getResultBadge(attempt.score, attempt.pass_threshold, attempt.question_limit)}
                                                </div>
                                                <div>
                                                    <h6 className="fw-bold mb-1 text-dark">{attempt.test_name}</h6>
                                                    <div className="text-muted small d-flex gap-3">
                                                        <span><FaCalendarAlt className="me-1"/> {new Date(attempt.finished_at).toLocaleDateString()}</span>
                                                        <span className="badge bg-light text-secondary border">{attempt.category || 'Generale'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center">
                                                <span className="fw-bold fs-5 me-2 text-dark">{attempt.score} <small className="text-muted fw-normal fs-6">/ {attempt.question_limit} pt</small></span>
                                                <FaChevronRight className="text-muted small ms-2" />
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-5 text-center text-muted">
                                        <FaHistory className="fs-1 mb-3 opacity-25"/>
                                        <p>Nessun test trovato con i filtri selezionati.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-lg-4 mb-4">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-header bg-white border-bottom p-3">
                            <h6 className="fw-bold text-success mb-0"><FaChartPie className="me-2" />Performance Categorie</h6>
                        </div>
                        <div className="card-body">
                            {categoryStats.length > 0 ? (
                                <ul className="list-group list-group-flush">
                                    {categoryStats.map((stat, idx) => {
                                        const rate = parseInt(stat.success_rate);
                                        let badgeClass = 'bg-danger text-danger';
                                        if(rate >= 70) badgeClass = 'bg-success text-success';
                                        else if(rate >= 50) badgeClass = 'bg-warning text-warning';

                                        return (
                                            <li key={idx} className="list-group-item d-flex justify-content-between align-items-center py-3 border-bottom-0">
                                                <div>
                                                    <div className="fw-bold text-dark">{stat.category || 'Generale'}</div>
                                                    <small className="text-muted">{stat.tests_count} Test svolti</small>
                                                </div>
                                                <span className={`badge bg-opacity-10 fs-6 rounded-pill px-3 ${badgeClass}`}>
                                                    {rate}% Successo
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <div className="text-center text-muted py-5">
                                    <small>Dati insufficienti per le statistiche.</small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* MODALI (CODICE PRESERVATO) */}
            {confirmStartTest && (
                <div className="modal d-block fade-in" style={{backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1070}}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content shadow border-0">
                            <div className="modal-header bg-primary text-white border-0">
                                <h5 className="modal-title fw-bold"><FaPlay className="me-2" />Avvia Test</h5>
                                <button className="btn-close btn-close-white" onClick={() => setConfirmStartTest(null)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <h4 className="fw-bold text-center mb-3">{confirmStartTest.name}</h4>
                                <div className="alert alert-light border text-center mb-4">
                                    <p className="mb-1 text-muted small">DESCRIZIONE</p>
                                    <p className="mb-0 fw-bold">{confirmStartTest.description || 'Nessuna descrizione.'}</p>
                                </div>
                                <div className="row g-2 text-center mb-4">
                                    <div className="col-4">
                                        <div className="p-2 border rounded bg-light">
                                            <FaClock className="text-primary mb-1 d-block mx-auto"/>
                                            <span className="fw-bold">{confirmStartTest.duration || 30}</span> <small>min</small>
                                        </div>
                                    </div>
                                    <div className="col-4">
                                        <div className="p-2 border rounded bg-light">
                                            <FaListOl className="text-primary mb-1 d-block mx-auto"/>
                                            <span className="fw-bold">{confirmStartTest.questions_count}</span> <small>dom</small>
                                        </div>
                                    </div>
                                    <div className="col-4">
                                        <div className="p-2 border rounded bg-light">
                                            <FaStar className="text-primary mb-1 d-block mx-auto"/>
                                            <span className="fw-bold">{confirmStartTest.pass_threshold || 60}%</span> <small>min</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="alert alert-warning small d-flex align-items-start">
                                    <FaInfoCircle className="me-2 mt-1 flex-shrink-0" />
                                    <div>Una volta avviato, il timer non può essere fermato. Assicurati di avere una connessione stabile.</div>
                                </div>
                            </div>
                            <div className="modal-footer border-0 bg-light justify-content-center p-3">
                                <button className="btn btn-secondary px-4 rounded-pill" onClick={() => setConfirmStartTest(null)}>Annulla</button>
                                <button className="btn btn-primary px-5 rounded-pill fw-bold shadow-sm" onClick={() => { navigate(`/execute-test/${confirmStartTest.id}`); setConfirmStartTest(null); }}>INIZIA ORA</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {selectedTestToStart && !confirmStartTest && (
                 <div className="modal d-block fade-in" style={{backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1070}}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content shadow border-0">
                            <div className="modal-header bg-primary text-white border-0">
                                <h5 className="modal-title fw-bold"><FaPlay className="me-2" />Avvia Test Pubblico</h5>
                                <button className="btn-close btn-close-white" onClick={() => setSelectedTestToStart(null)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <h4 className="fw-bold text-center mb-3">{selectedTestToStart.name}</h4>
                                <div className="row g-2 text-center mb-4">
                                    <div className="col-6">
                                        <div className="p-2 border rounded bg-light">
                                            <FaClock className="text-primary mb-1 d-block mx-auto"/>
                                            <span className="fw-bold">{selectedTestToStart.duration || 30}</span> <small>min</small>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="p-2 border rounded bg-light">
                                            <FaListOl className="text-primary mb-1 d-block mx-auto"/>
                                            <span className="fw-bold">{selectedTestToStart.question_limit || 10}</span> <small>dom</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="d-grid">
                                    <button className="btn btn-primary rounded-pill fw-bold shadow-sm" onClick={() => { navigate(`/execute-test/${selectedTestToStart.id}`); setSelectedTestToStart(null); }}>INIZIA ORA</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {resultModalData && (
                <div className="modal d-block fade-in" style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060}}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content shadow border-0">
                            <div className="modal-header bg-white border-bottom">
                                <div>
                                    <h5 className="modal-title fw-bold text-primary"><FaTrophy className="me-2 text-warning"/>Esito: {resultModalData.testName}</h5>
                                    <small className="text-muted">Eseguito il {new Date(resultModalData.date).toLocaleString()}</small>
                                </div>
                                <div className="text-end">
                                    <h3 className="fw-bold mb-0 text-dark">{resultModalData.score} Punti</h3>
                                </div>
                            </div>
                            <div className="modal-body bg-light p-4">
                                {resultModalData.questions.map((q, idx) => (
                                    <div key={idx} className={`card mb-3 border-0 shadow-sm ${q.is_correct == 1 ? 'border-start border-success border-4' : 'border-start border-danger border-4'}`}>
                                        <div className="card-body">
                                            <h6 className="fw-bold mb-3 text-dark"><span className="badge bg-light text-dark border me-2">#{idx + 1}</span>{q.question_text}</h6>
                                            <div className={`p-3 rounded mb-2 d-flex align-items-center justify-content-between ${q.is_correct == 1 ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'}`}>
                                                <div><span className="fw-bold me-2">{q.is_correct == 1 ? 'TUA RISPOSTA:' : 'HAI RISPOSTO:'}</span>{q.user_answer_text}</div>
                                                {q.is_correct == 1 ? <FaCheckCircle className="fs-4"/> : <FaTimesCircle className="fs-4"/>}
                                            </div>
                                            {q.is_correct != 1 && (
                                                <div className="mt-3 bg-white border rounded p-3">
                                                    <div className="text-success mb-2"><strong><FaCheckCircle className="me-2"/>Risposta Corretta:</strong> <span className="ms-1">{q.correct_answer_text}</span></div>
                                                    {q.explanation && <div className="alert alert-info d-flex align-items-start mb-0 mt-2"><FaExclamationCircle className="me-2 mt-1 flex-shrink-0"/><div><strong>Spiegazione:</strong><div className="small mt-1">{q.explanation}</div></div></div>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="modal-footer bg-white">
                                <button className="btn btn-secondary px-4" onClick={() => setResultModalData(null)}>Chiudi</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;